import { State, Action, StateContext, Store } from '@ngxs/store';
import { EmailActions } from './actions';
import { StakeholderEmail } from 'src/app/shared/model/stakeholder-email';
import { EmailService } from 'src/app/shared/services/email.service';
import { EMPTY, forkJoin, of, throwError } from 'rxjs'
import { map, catchError, delay, tap, concatMap, filter } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { HistoryActions } from '../history/actions';
import { Navigate } from '@ngxs/router-plugin';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material';
import { NgZone } from '@angular/core';
import { ProcessingActions } from '../processing/actions';


const defaultState: StakeholderEmail = {
  Id: '',
  Type: 'StakeholderEmail',
  CreatedTimestamp: '',
  ModifiedTimestamp: '',
  EntityDate: '',
  UserId: '',
  ToAddresses: [],
  CcAddresses: [],
  BccAddresses: [],
  AllStakeholderAddresses: [],
  AdminAddresses: [],
  Subject: '',
  FromDisplay: '',
  HtmlStart: '',
  Header: '',
  Message: '',
  Footer: '',
  HtmlEnd: '',
  Status: 0,
  StatusDescription: '',
  FilterId: '',
  PersonId: '',
  project_search: '',
  stakeholder_search: '',
  ProjectId: '',
  IsOnlyActive: false,
  PE_Ids: '',
  Organization_Type_Ids: '',
  Stakeholder_Type_Ids: '',
  SentDate: '',
  BCCDesc: '',
  CCDesc: '',
  ToDesc: '',
  Recipients: [],
}

@State<StakeholderEmail>({
  name: 'stakeholderEmail',
  defaults: defaultState,
})

export class EmailState {
  constructor(
    private emailService: EmailService,
    private snackBar: MatSnackBar,
    private store: Store,
    private zone: NgZone
  ) { }

  @Action(EmailActions.GetEmailRecord)
  getEmailRecord(ctx: StateContext<StakeholderEmail>, action: EmailActions.GetEmailRecord) {
    return this.emailService.getEmailById(action.emailId).pipe(
      map(result => {
        ctx.setState(result);
      }),
      catchError(this.handleError)
    );
  }

  @Action(EmailActions.GetEmailRecordMessageOnly)
  getEmailRecordMessageOnly(ctx: StateContext<StakeholderEmail>, action: EmailActions.GetEmailRecordMessageOnly) {
    const state = ctx.getState();
    return this.emailService.getEmailById(action.messageId).pipe(
      map(result => {
        // check for default State, ie no data, then make request
        if (state.Id === defaultState.Id){
          this.emailService.getEmailById(action.emailId).subscribe(data => {
            data.HtmlStart = result.HtmlStart,
            data.Header = result.Header,
            data.Message = result.Message,
            data.Footer = result.Footer,
            data.HtmlEnd = result.HtmlEnd
            ctx.setState(data);
          });
        } else {
        // update existing StakeholderEmail message props using another email message
        ctx.setState({
          ...state,
          HtmlStart: result.HtmlStart,
          Header: result.Header,
          Message: result.Message,
          Footer: result.Footer,
          HtmlEnd: result.HtmlEnd
        });
        }

      }),
      catchError(this.handleError)
    );
  }


  @Action(EmailActions.SaveEmailRecord)
  saveEmailRecord(ctx: StateContext<StakeholderEmail>, action: EmailActions.SaveEmailRecord) {
    const state = ctx.getState();
    return this.emailService.updateEmail(state).pipe(
      map(result => {
        ctx.dispatch(new HistoryActions.GetEmailList(0,0,0));
        ctx.setState(result);

        // fixes issue with snackbar positioning
        this.showSnackBarMsg('Draft saved!', 'bottom', 'center');
      }),
      catchError(this.handleError)
    );
  }

  @Action(EmailActions.SendEmail)
  sendEmail(ctx: StateContext<StakeholderEmail>, action: EmailActions.SendEmail) {
    const state = ctx.getState();
    if (state.Message) {
      return this.emailService.sendEmail(state).pipe(
        delay(2000),
        map(result => {
          ctx.dispatch(new HistoryActions.GetEmailList());
          ctx.setState(defaultState);
          this.store.dispatch(new ProcessingActions.SetEmailSending(false));
          // ctx.dispatch(new Navigate(['/history']));

          this.zone.run(() => { // fixes issue with snackbar positioning

            const snackbarRef = this.snackBar.open('Email added to Send Queue', 'OK', {
              duration: 3000,
              verticalPosition: 'bottom',
              horizontalPosition: 'center'
            });

            snackbarRef.onAction().subscribe(() => {
              snackbarRef.dismiss();
            });

            snackbarRef.afterDismissed().subscribe(() => {
              ctx.dispatch(new Navigate(['/email']));
            });
          });

        }),
        catchError(error => {
          this.displayError(error);
          return this.handleError(error);
        })
      );
    } else {
      // fixes issue with snackbar positioning
      this.showSnackBarMsg('Please enter a message before sending', 'bottom', 'center');
    }

  }

  // divides Bcc's into batches
  @Action(EmailActions.SendEmailBatches)
  sendEmailBatches(ctx: StateContext<StakeholderEmail>, action: EmailActions.SendEmailBatches) {
    const state = ctx.getState();
    if (state.Message) {
      // create the batches, will throw error if batch size is too large
      const totalBccCount = state.BccAddresses.length;
      let sentBccCount = 0;
      return forkJoin(
        this.emailService.createEmailBccBatches(state, action.batchSize).pipe(
          tap(() =>{
            this.store.dispatch(new ProcessingActions.Reset());
            this.store.dispatch(new ProcessingActions.SetEmailBatchSending(true));
          }),
          catchError(error => {
            this.store.dispatch(new ProcessingActions.SetError(error));
            return of(null);
          }),
          filter(batch => batch !== null),
          concatMap(batch => this.emailService.sendEmail(batch, true).pipe(
            tap(batch => {
              if(batch.BccAddresses.length > 0)
              {
                sentBccCount = sentBccCount + batch.BccAddresses.length;
                console.log(`Bcc Batch Size: ${batch.BccAddresses.length}, email w/ id=${batch.Id}`);
              } else if(batch.Recipients.length > 0) {
                sentBccCount = sentBccCount + batch.Recipients.length; 
                console.log(`Recipients Batch Size: ${batch.BccAddresses.length}, email w/ id=${batch.Id}`);
              } else {
                sentBccCount = sentBccCount + 1;
                console.log(`Unknown Batch Size: ${sentBccCount}, email w/ id=${batch.Id}`);
              }
              console.log(`Current Sent Count: ${sentBccCount} of ${totalBccCount}`);
              let batchNum = Math.ceil(sentBccCount / action.batchSize);
              let batchTotal = Math.ceil(totalBccCount / action.batchSize);
              let message = `Sending Batch: ${batchNum} of ${batchTotal}`;
              this.store.dispatch(new ProcessingActions.SetEmailBatchSendingMessage(true, message));
            }),
            catchError(error => {
              this.store.dispatch(new ProcessingActions.SetError(error));
              return of(null);
            }),
            delay(3000), // 3 second delay
          )),
        )
      ).pipe(
        delay(2000),
        tap(() => {
            this.store.dispatch(new ProcessingActions.SetEmailBatchSending(false));
            this.store.dispatch(new ProcessingActions.ClearMessage());
            // fixes issue with snackbar positioning
            this.showSnackBarMsg('Email Batches added to Send Queue!', 'top', 'center');
            if (sentBccCount !== totalBccCount){
              const error = `Sent ${sentBccCount} of ${totalBccCount}`;
              this.store.dispatch(new ProcessingActions.SetError(error));
              console.error(error);
            }
          }
        )
      )
    } else {
      // fixes issue with snackbar positioning
      this.showSnackBarMsg('Please enter a message before sending', 'bottom', 'center');
    }

  }

  @Action(EmailActions.EditEmailProp)
  editEmailProp(ctx: StateContext<StakeholderEmail>, action: EmailActions.EditEmailProp) {
    const state = ctx.getState();

    ctx.setState({
      ...state,
      ...action.payload
    });
  }

  @Action(EmailActions.AddTo)
  addTo(ctx: StateContext<StakeholderEmail>, action: EmailActions.AddTo) {
    const state = ctx.getState();

    const addresses = state.ToAddresses ? state.ToAddresses : [];
    ctx.setState({
      ...state,
      ToAddresses: [...addresses, action.email]
    });
  }

  @Action(EmailActions.RemoveTo)
  removeTo(ctx: StateContext<StakeholderEmail>, action: EmailActions.RemoveTo) {
    const state = ctx.getState();

    const index = state.ToAddresses.indexOf(action.email);

    if (index >= 0) {
      const to = [
        ...state.ToAddresses.slice(0, index),
        ...state.ToAddresses.slice(index + 1)
      ];

      ctx.setState({
        ...state,
        ToAddresses: to
      });
    }
  }

  @Action(EmailActions.AddCc)
  addCc(ctx: StateContext<StakeholderEmail>, action: EmailActions.AddCc) {
    const state = ctx.getState();

    const addresses = state.CcAddresses ? state.CcAddresses : [];
    ctx.setState({
      ...state,
      CcAddresses: [...addresses, action.email]
    });
  }

  @Action(EmailActions.RemoveCc)
  removeCc(ctx: StateContext<StakeholderEmail>, action: EmailActions.RemoveCc) {
    const state = ctx.getState();

    const index = state.CcAddresses.indexOf(action.email);

    if (index >= 0) {
      const ccs = [
        ...state.CcAddresses.slice(0, index),
        ...state.CcAddresses.slice(index + 1)
      ];

      ctx.setState({
        ...state,
        CcAddresses: ccs
      });
    }
  }

  @Action(EmailActions.AddBcc)
  addBcc(ctx: StateContext<StakeholderEmail>, action: EmailActions.AddBcc) {
    const state = ctx.getState();

    const addresses = state.BccAddresses ? state.BccAddresses : [];
    ctx.setState({
      ...state,
      BccAddresses: [...addresses, action.email]
    });
  }

  @Action(EmailActions.RemoveBcc)
  removeBcc(ctx: StateContext<StakeholderEmail>, action: EmailActions.RemoveBcc) {
    const state = ctx.getState();

    const index = state.BccAddresses.indexOf(action.email);

    if (index >= 0) {
      const bccs = [
        ...state.BccAddresses.slice(0, index),
        ...state.BccAddresses.slice(index + 1)
      ];

      ctx.setState({
        ...state,
        BccAddresses: bccs
      });
    }
  }

  private displayError(error: HttpErrorResponse){
    this.store.dispatch(new ProcessingActions.SetEmailSending(false));
    if (error.status === 503){
      this.store.dispatch(new ProcessingActions.SetEmailSending(true)); // disable send btn
      setTimeout(() => {
        this.store.dispatch(new ProcessingActions.SetMessage('Email may still be sending in the background.  Please confirm delivery before sending again.'));
        this.store.dispatch(new ProcessingActions.SetEmailSending(false));
        setTimeout(() =>{
          this.store.dispatch(new ProcessingActions.ClearMessage());
        }, 5000)
      }, 5000)
    }
    else {
      this.store.dispatch(new ProcessingActions.SetError(`Internal Server Error: Please Contact PIMA Support`));
    }
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      errorMessage = 'An error occurred:' + error.error.message;
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      errorMessage = error.error;
      console.error(
        `Backend returned code ${error.status}, ` + `body was: ${error.error}`
      );
    }
    // return an observable with a user-facing error message
    return throwError(errorMessage);
  }

  private showSnackBarMsg(msg: string, vertPosition: MatSnackBarVerticalPosition, horizPosition: MatSnackBarHorizontalPosition){
    this.zone.run(() => {

      const snackbarErrRef = this.snackBar.open(msg, 'OK', {
        duration: 3000,
        verticalPosition: vertPosition,
        horizontalPosition: horizPosition
      });

      snackbarErrRef.onAction().subscribe(() => {
        snackbarErrRef.dismiss();
      });

    });
  }

}
